import type { AccountTypeBadgeVariant } from '@/modules/projects/components/AccountTypeBadge'
import { formatAccountType } from '@/modules/projects/labels'
import type { Account, AccountType } from './types'

export const NO_ACCOUNT_LABEL = 'Account not identified'

// The bank prints only the last four, so the mask stands in for the digits it never showed.
export const NUMBER_MASK = '••'

export const accountTypeWord = (accountType: AccountType | null) =>
  accountType ? formatAccountType(accountType) : 'Type not known'

// The app's account pill is keyed by the same words. A type the machine never read has no colour
// of its own, so the pill falls back to its plain one.
export const accountTypePill = (accountType: AccountType | null) =>
  accountType
    ? (formatAccountType(accountType) as AccountTypeBadgeVariant)
    : undefined

export const accountOf = (accounts: Account[], accountId: string | null) =>
  accounts.find((account) => account.id === accountId) ?? null

export const firstNumberOf = (accounts: Account[], accountId: string | null) =>
  accountOf(accounts, accountId)?.numbers[0] ?? null

// A combined mailing prints two numbers on one page, so it keeps both and its pages are never
// split across two lanes.
export const accountNumbersText = (account: Account) =>
  account.numbers.join(' + ')

// The same numbers for a line of running text, where nothing can style the mask apart.
export const maskedNumbersText = (account: Account) =>
  account.numbers.map((number) => `${NUMBER_MASK} ${number}`).join(' + ')

// The joined form for a line of running text. It is built here and never stored.
export const accountLabel = (accounts: Account[], accountId: string | null) => {
  const account = accountOf(accounts, accountId)
  if (!account) return NO_ACCOUNT_LABEL
  const number = maskedNumbersText(account)
  return account.accountType
    ? `${accountTypeWord(account.accountType)} ${number}`
    : number
}

export type AccountDetails = Omit<Account, 'id'>
